import React, { createContext, useEffect, useState, useRef } from "react";
import { hot } from "react-hot-loader/root";
import AppRouter from "./routes/AppRouter";
import "antd/dist/antd.css";
import "./index.less";
import CallPopup from "./components/CallPopup";
import OutgoingCallPopup from "./components/CallPopup/OutGoingCallPopup";
import RingtonePlayer from "./components/CallPopup/RingtonePlayer";
import {
  getPiopiy,
  piopiyLogin,
  logoutPiopiy,
  mergeCall,
  completeOutboundTransfer,
  toIndianMobile10,
  piopiyDialUser,
} from "./utils/piopiy";
import { platformApi } from "./api";
import { message } from "antd";
import { withRouter } from "react-router-dom";

const redirectTargetRef = { current: null };

// Keep toasts below the fixed TeleCMI call bar
message.config({ top: 64, duration: 3, maxCount: 1 });

export var callContext = createContext({
  mobileNumber: "",
  setMobileNumber: () => {},
});

const App = () => {
  const [showOutgoing, setShowOutgoing] = useState(false);
  const [showInComming, setShowInComming] = useState(false);
  const [isIncomingCall, setIsIncomingCall] = useState(false);
  const [isAttended, setIsAttended] = useState(false);
  const [mobileNumber, setMobileNumber] = useState("");
  const [name, setName] = useState("");
  const [isRinging, setIsRinging] = useState(false);
  const [preferredExecutiveId, setPreferredExecutiveId] = useState(null);
  const [transferPending, setTransferPending] = useState(false);
  const [transferStatusText, setTransferStatusText] = useState("");

  const isIncomingRef = useRef(false);
  const isAttendedRef = useRef(false);
  const transferPendingRef = useRef(false);
  const transferMergedRef = useRef(false);
  /** 'inbound' | 'outbound' — outbound redirect completes via agent hangup, not merge DTMF */
  const callDirectionRef = useRef(null);
  const outboundRedirectCompletingRef = useRef(false);
  /** Notification Call: dial only after Piopiy listeners are attached */
  const pendingNotifyCallRef = useRef(null);
  const piopiyListenersReadyRef = useRef(false);
  const mobileNumberRef = useRef("");
  /** Prevent double POST /call-redirect-missed for one redirect */
  const redirectMissReportedRef = useRef(false);
  /** Prevent double POST /call-agent-missed for one softphone ring */
  const agentMissReportedRef = useRef(false);

  useEffect(() => {
    isIncomingRef.current = isIncomingCall;
  }, [isIncomingCall]);

  useEffect(() => {
    isAttendedRef.current = isAttended;
  }, [isAttended]);

  useEffect(() => {
    transferPendingRef.current = transferPending;
  }, [transferPending]);

  useEffect(() => {
    mobileNumberRef.current = mobileNumber;
  }, [mobileNumber]);

  const reportAgentSoftphoneMiss = (reason) => {
    if (agentMissReportedRef.current) return;
    if (callDirectionRef.current !== "inbound") return;
    if (isAttendedRef.current) return;
    const customerPhone = String(mobileNumberRef.current || "").replace(
      /\D/g,
      ""
    );
    if (!customerPhone) return;
    agentMissReportedRef.current = true;

    let teleCMIUserID = null;
    try {
      const piopiyState = localStorage.getItem("piopiyLoginState");
      if (piopiyState) {
        teleCMIUserID = JSON.parse(piopiyState)?.username || null;
      }
    } catch (e) {
      /* ignore */
    }

    console.log("[TeleCMI] agent softphone miss", reason, customerPhone);
    platformApi
      .post("/api/telecmi/call-agent-missed", {
        customerPhone,
        teleCMIUserID,
      })
      .catch((err) =>
        console.error("Failed to report agent softphone miss:", err)
      );
  };

  const clearTransferFlags = () => {
    setTransferPending(false);
    setTransferStatusText("");
    transferMergedRef.current = false;
    message.destroy("call-redirect");
  };

  const postRedirectComplete = (target) => {
    const customerPhone = String(mobileNumberRef.current || "").replace(
      /\D/g,
      ""
    );
    platformApi
      .post("/api/telecmi/call-redirect-complete", {
        customerPhone,
        employeePhone: target?.phone || null,
        employeeId: target?.id || null,
      })
      .catch((err) =>
        console.error("Failed to clear call redirect pending:", err)
      );
  };

  /**
   * TeleCMI often never sends outbound missed CDR for personal-mobile redirect.
   * Board hang-up / transfer ended without Connect → create employee miss notify.
   */
  const reportIncompleteRedirectMiss = (reason) => {
    const target = redirectTargetRef.current;
    if (!target?.id) return;
    if (transferMergedRef.current) return;
    if (outboundRedirectCompletingRef.current) return;
    if (redirectMissReportedRef.current) return;
    redirectMissReportedRef.current = true;

    const customerPhone = String(mobileNumberRef.current || "").replace(
      /\D/g,
      ""
    );
    console.log("[TeleCMI] reporting incomplete redirect miss:", reason, {
      employeeId: target.id,
      customerPhone,
    });

    platformApi
      .post("/api/telecmi/call-redirect-missed", {
        employeeId: target.id,
        employeeName: target.name,
        employeePhone: target.phone,
        customerPhone,
      })
      .then((res) => {
        console.log("[TeleCMI] call-redirect-missed result", res?.data);
      })
      .catch((err) =>
        console.error("Failed to report redirect miss notify:", err)
      );
  };

  const finishIncompleteRedirect = (reason) => {
    reportIncompleteRedirectMiss(reason);
    setIsAttended(false);
    setShowOutgoing(false);
    setShowInComming(false);
    setIsRinging(false);
    setPreferredExecutiveId(null);
    callDirectionRef.current = null;
    redirectTargetRef.current = null;
    outboundRedirectCompletingRef.current = false;
    clearTransferFlags();
  };

  const finishTransferSuccess = () => {
    transferMergedRef.current = true;
    outboundRedirectCompletingRef.current = false;
    const target = redirectTargetRef.current;
    postRedirectComplete(target);
    message.success({
      content: "Customer connected to employee",
      key: "call-redirect",
      duration: 3,
    });
    setIsAttended(false);
    setShowOutgoing(false);
    setShowInComming(false);
    setIsRinging(false);
    setPreferredExecutiveId(null);
    callDirectionRef.current = null;
    redirectTargetRef.current = null;
    redirectMissReportedRef.current = false;
    clearTransferFlags();
  };

  /** Board pressed Hang up / Reject while redirect in progress → employee miss;
   *  unanswered inbound Reject → board softphone miss */
  const onRejectHangup = () => {
    setIsRinging(false);
    if (
      transferPendingRef.current &&
      redirectTargetRef.current &&
      !transferMergedRef.current &&
      !outboundRedirectCompletingRef.current
    ) {
      finishIncompleteRedirect("board-hangup");
      return;
    }
    if (
      callDirectionRef.current === "inbound" &&
      !isAttendedRef.current
    ) {
      reportAgentSoftphoneMiss("agent-reject");
    }
  };

  // CRITICAL FIX: Use state to track piopiy instance properly
  const [piopiy, setPiopiy] = useState(() => getPiopiy());

  const reconnectPiopiy = async () => {
    const selectedUserId = localStorage.getItem("selectedTelecmiUserId");
    const piopiyState = localStorage.getItem("piopiyLoginState");

    if (selectedUserId && piopiyState) {
      try {
        const { username } = JSON.parse(piopiyState);
        const instance = await piopiyLogin(null, username, true);
        // CRITICAL FIX: Update state with the returned instance
        setPiopiy(instance);
        // Re-assert DB + TeleCMI portal Online after refresh/reconnect
        try {
          await platformApi.post(
            `/api/telecmi/teleUser/ensureOnline/${selectedUserId}`
          );
        } catch (syncErr) {
          console.error("Failed to re-assert TeleCMI online:", syncErr);
        }
      } catch (err) {
        console.error("Failed to reconnect:", err);
        setPiopiy(null);
      }
    }
  };

  const flushPendingNotifyCall = (instance) => {
    const pending = pendingNotifyCallRef.current;
    const piopiyInstance = instance || getPiopiy();
    if (!pending || !piopiyInstance || typeof piopiyInstance.call !== "function") {
      return;
    }
    pendingNotifyCallRef.current = null;
    try {
      setMobileNumber(pending.dialNumber);
      piopiyDialUser(
        piopiyInstance,
        pending.dialNumber,
        pending.uniqueId,
        "Call-Back",
        pending.phone10
      );
      message.success("Calling…");
      window.dispatchEvent(
        new CustomEvent("telecmi-call-started", { detail: { ok: true } })
      );
    } catch (err) {
      console.error("Notification dial flush failed:", err);
      window.dispatchEvent(
        new CustomEvent("telecmi-call-started", {
          detail: { ok: false, error: err?.message || "Failed to place call" },
        })
      );
    }
  };

  useEffect(() => {
    reconnectPiopiy();

    // Notification Call: queue dial + force App piopiy state/listeners
    const onPlaceCall = (e) => {
      pendingNotifyCallRef.current = e?.detail || null;
      const instance = getPiopiy();
      if (piopiyListenersReadyRef.current && instance) {
        flushPendingNotifyCall(instance);
      } else if (instance) {
        setPiopiy(instance);
      }
    };
    const onPiopiyReady = () => {
      const instance = getPiopiy();
      if (instance) setPiopiy(instance);
    };
    window.addEventListener("telecmi-place-call", onPlaceCall);
    window.addEventListener("piopiy-ready", onPiopiyReady);

    // CRITICAL FIX: Reduce polling frequency to prevent race conditions
    const interval = setInterval(() => {
      const instance = getPiopiy();
      setPiopiy((prev) => {
        if (prev !== instance) {
          // console.log("Piopiy instance changed:", instance);
          return instance;
        }
        return prev;
      });
    }, 1000);

    return () => {
      clearInterval(interval);
      window.removeEventListener("telecmi-place-call", onPlaceCall);
      window.removeEventListener("piopiy-ready", onPiopiyReady);
    };
  }, []);

  const handleCustomerData = async (phoneNumber) => {
    if (!isIncomingRef.current) return;

    const phone10 = toIndianMobile10(phoneNumber);
    if (!phone10) {
      message.error("Invalid caller number");
      return;
    }

    try {
      // Same phone lookup as the incoming popup (not branch-filtered list search).
      // Non-customers without quotations were missed by /api/customer/get + getCustomer raw SQL.
      const phoneRes = await platformApi.get(`/api/customer/phone/${phone10}`);
      const rows = phoneRes?.data?.response?.data;
      const customer = Array.isArray(rows) ? rows[0] : null;

      // Optional: preferred executive for redirect smart-sort (non-blocking if it fails)
      try {
        const execRes = await platformApi.get(
          `/api/telecmi/getCustomer/${phone10}`
        );
        const execId = execRes?.data?.preferredExecutiveId;
        if (execId) setPreferredExecutiveId(execId);
      } catch (e) {
        /* ignore */
      }

      if (!customer) {
        message.info("New customer");
        localStorage.setItem(
          "customerState",
          JSON.stringify({ phone: phone10, editable: false })
        );
        window.open("/autoadmin/customer_details", "_blank");
        return;
      } else {
        localStorage.setItem(
          "customerState",
          JSON.stringify({
            phone: phone10,
            name: customer.name,
            id: customer.id,
            editable: true,
          })
        );
        window.open("/autoadmin/customer_details", "_blank");
      }
      // if(bookings.length>0 && bookings[0].bookingStatus=="SOLD"){
      //   localStorage.setItem(
      //     "customerState",
      //     JSON.stringify({ phone: phoneNumber,name:customer.name, editable: true })
      //   );
      //   window.open("/autoadmin/customer_details", "_blank");
      // } else if(bookings.length>0 && bookings[0].bookingStatus=="REJECTED"){
      //   localStorage.setItem(
      //     "customerState",
      //     JSON.stringify({ phone: phoneNumber,name:customer.name, editable: true })
      //   );
      //   window.open("/autoadmin/customer_details", "_blank");
      // } else if (bookings?.length > 0) {
      //   localStorage.setItem(
      //     "bookingState",
      //     JSON.stringify({ addFlag: true, id: bookings[0].id, bookingStatus:bookings[0].bookingStatus })
      //   );
      //   window.open("/autoadmin/booking_register", "_blank");
      // } else if (jobOrders?.length > 0) {
      //   localStorage.setItem(
      //     "jobOrderState",
      //     JSON.stringify({ jobId: jobOrders[0].id })
      //   );
      //   window.open("/autoadmin/job_order", "_blank");
      // } else if (quotations?.length > 0) {
      //   const route = `/autoadmin/follow-ups/${phoneNumber}/fupdate/${quotations[0].scheduleDateAndTime}`;
      //   window.open(route, "_blank");
      // } else {
      //   localStorage.setItem(
      //     "customerState",
      //     JSON.stringify({ phone: phoneNumber,name:customer.name, editable: true })
      //   );
      //   window.open("/autoadmin/customer_details", "_blank");
      // }
    } catch (error) {
      console.error("Error fetching customer data:", error);
      message.error("Error fetching customer data");
    }
    setIsIncomingCall(false);
  };

  useEffect(() => {
    if (!piopiy) return;

    // console.log("Setting up piopiy event listeners for instance:", piopiy);

    let mobile;

    const handleIncomingCall = async (object) => {
      setIsIncomingCall(true);
      callDirectionRef.current = "inbound";
      setPreferredExecutiveId(null);
      setTransferPending(false);
      setTransferStatusText("");
      transferMergedRef.current = false;
      outboundRedirectCompletingRef.current = false;
      agentMissReportedRef.current = false;
      isAttendedRef.current = false;
      // console.log("Incoming call received:", object);
      // Last-10 normalize: +9179… / 9179… / 079… all become 10 digits (not slice(2))
      const phone = toIndianMobile10(object.from);
      mobile = phone;
      setMobileNumber(phone || String(object.from || ""));
      setIsRinging(true);

      if (!phone) {
        setName("New Customer");
        setShowInComming(true);
        return;
      }

      try {
        const response = await platformApi.get(`/api/customer/phone/${phone}`);
        const rows = response?.data?.response?.data;
        const customer = Array.isArray(rows) ? rows[0] : null;
        if (customer?.name) {
          setName(customer.name);
        } else if (customer) {
          setName("Customer Exists");
        } else {
          setName("New Customer");
        }
      } catch (err) {
        console.error("API error:", err);
        setName("New Customer");
        message.error("Something went wrong");
      }

      setShowInComming(true);
      // handleCustomerData(phone);
    };

    const handleLogin = (object) => {
      console.log("Login successful", object);
    };

    const handleTrying = async (object) => {
      if (object.code === 100) {
        callDirectionRef.current = "outbound";
        setShowOutgoing(true);
      }
    };

    const handleAnswered = (object) => {
      if (object.code === 200) {
        setIsAttended(true);
        isAttendedRef.current = true;
        setIsRinging(false);
        handleCustomerData(mobile);
      }
    };

    const resetCallState = () => {
      // Outbound redirect complete = agent hangup; treat hangup/ended as success
      if (
        transferPendingRef.current &&
        outboundRedirectCompletingRef.current
      ) {
        console.log("[TeleCMI] outbound redirect completed via agent hangup");
        outboundRedirectCompletingRef.current = false;
        finishTransferSuccess();
        return;
      }

      // TeleCMI fires hangup/ended during transfer (*9 hold). Ignore mid-transfer
      // unless board already chose Hang up via onRejectHangup (explicit).
      if (transferPendingRef.current) {
        console.log(
          "[TeleCMI] hangup/ended ignored during redirect — waiting for Connect or Hang up"
        );
        return;
      }

      if (
        callDirectionRef.current === "inbound" &&
        !isAttendedRef.current
      ) {
        reportAgentSoftphoneMiss("customer-or-remote-cancel");
      }

      setIsAttended(false);
      isAttendedRef.current = false;
      setShowOutgoing(false);
      setShowInComming(false);
      setIsRinging(false);
      setPreferredExecutiveId(null);
      callDirectionRef.current = null;
      clearTransferFlags();
    };

    const tryAutoMergeInbound = (reason) => {
      // Only inbound uses DTMF merge. Outbound must NOT auto-merge (cuts employee).
      if (callDirectionRef.current !== "inbound") return;
      if (transferMergedRef.current) return;
      console.log("[TeleCMI] auto-merge inbound:", reason);
      try {
        mergeCall();
        transferMergedRef.current = true;
        setTransferStatusText(
          "Connecting customer… if no audio, click green Connect"
        );
      } catch (err) {
        console.error("[TeleCMI] merge failed", err);
        transferMergedRef.current = false;
        setTransferStatusText(
          "When employee answers, click green Connect"
        );
      }
    };

    const handleTransfer = (object) => {
      // Inbound: transfer() then merge() (DTMF 0).
      // Outbound: transfer() then agent hangup to bridge — merge() cuts employee.
      console.log("[TeleCMI] transfer event", object, {
        direction: callDirectionRef.current,
      });
      const code = object?.code;
      const state = String(
        object?.state || object?.status || object?.msg || ""
      ).toLowerCase();

      setTransferPending(true);

      if (code === 100 || object?.global === true) {
        if (state === "init") {
          setTransferStatusText(
            callDirectionRef.current === "outbound"
              ? "Ringing employee… when they answer, click green Connect (completes redirect)"
              : "Ringing employee… when they answer, click green Connect"
          );
          return;
        }

        if (state === "started") {
          setTransferStatusText(
            callDirectionRef.current === "outbound"
              ? "Employee answered — click green Connect to join them with customer"
              : "Employee answered — connecting…"
          );
          tryAutoMergeInbound("transfer:started");
          return;
        }

        if (state === "bridged") {
          finishTransferSuccess();
          return;
        }

        if (state === "ended") {
          if (transferMergedRef.current || outboundRedirectCompletingRef.current) {
            finishTransferSuccess();
          } else if (redirectTargetRef.current) {
            // Transfer leg ended without Connect — employee never joined
            finishIncompleteRedirect("transfer-ended");
          }
          return;
        }

        if (!state) {
          setTransferStatusText(
            "Redirect in progress… when employee answers, click green Connect"
          );
        }
      }
    };

    const handleLogout = (object) => {
      if (object.code === 200) {
        // localStorage.removeItem('selectedTelecmiUserId');
        logoutPiopiy();
        message.success("Logout piopiy");
      }
    };

    const handleError = (error) => {
      // Ignore DTMF/transfer command noise while redirect is in progress
      console.error("Piopiy error:", error);
      if (transferPendingRef.current) {
        return;
      }
    };

    // Add all event listeners
    piopiy.on("inComingCall", handleIncomingCall);
    piopiy.on("login", handleLogin);
    piopiy.on("trying", handleTrying);
    piopiy.on("answered", handleAnswered);
    piopiy.on("ended", resetCallState);
    piopiy.on("hangup", resetCallState);
    piopiy.on("transfer", handleTransfer);
    piopiy.on("logout", handleLogout);
    piopiy.on("error", handleError);

    piopiyListenersReadyRef.current = true;
    // Notification Call: dial only after trying/answered/etc are bound
    flushPendingNotifyCall(piopiy);

    // Cleanup function
    return () => {
      piopiyListenersReadyRef.current = false;
      piopiy.off("inComingCall", handleIncomingCall);
      piopiy.off("login", handleLogin);
      piopiy.off("trying", handleTrying);
      piopiy.off("answered", handleAnswered);
      piopiy.off("ended", resetCallState);
      piopiy.off("hangup", resetCallState);
      piopiy.off("transfer", handleTransfer);
      piopiy.off("logout", handleLogout);
      piopiy.off("error", handleError);
    };
  }, [piopiy]);

  const registerRedirectAttendant = (emp) => {
    const target = emp || redirectTargetRef.current;
    if (!target?.id) return;
    const customerPhone = String(mobileNumber || "").replace(/\D/g, "");
    platformApi
      .post("/api/telecmi/call-redirect", {
        employeeId: target.id,
        employeeName: target.name,
        employeePhone: target.phone,
        customerPhone,
        direction: callDirectionRef.current,
      })
      .catch((err) =>
        console.error("Failed to register call redirect attendant:", err)
      );
  };

  const handleTransferStarted = (emp) => {
    transferMergedRef.current = false;
    outboundRedirectCompletingRef.current = false;
    redirectMissReportedRef.current = false;
    redirectTargetRef.current = emp || null;
    setTransferPending(true);
    setTransferStatusText(
      callDirectionRef.current === "outbound"
        ? `Ringing ${emp?.name || "employee"}… when they answer, click green Connect`
        : `Ringing ${emp?.name || "employee"}… when they answer, click green Connect`
    );
    registerRedirectAttendant(emp);
  };

  const handleMergeTransfer = () => {
    const isOutbound = callDirectionRef.current === "outbound";
    try {
      registerRedirectAttendant(redirectTargetRef.current);

      if (isOutbound) {
        // Root cause fix: DTMF merge (0) on outbound drops the employee leg.
        // Complete attended transfer by agent hangup so TeleCMI bridges customer↔employee.
        outboundRedirectCompletingRef.current = true;
        transferMergedRef.current = true;
        setTransferStatusText(
          "Completing redirect — connecting customer to employee…"
        );
        message.loading({
          content: "Completing redirect…",
          key: "call-redirect",
          duration: 0,
        });
        completeOutboundTransfer();
        return;
      }

      mergeCall();
      transferMergedRef.current = true;
      setTransferStatusText("Connecting customer to employee…");
      message.success({
        content: "Connect sent — parties should hear each other now",
        key: "call-redirect",
        duration: 3,
      });
      setTimeout(() => {
        if (!transferMergedRef.current) return;
        finishTransferSuccess();
      }, 2500);
    } catch (err) {
      console.error(err);
      outboundRedirectCompletingRef.current = false;
      transferMergedRef.current = false;
      message.error("Connect failed — try again while still on the call");
    }
  };

  return (
    <div>
      <callContext.Provider value={{ mobileNumber, setMobileNumber }}>
        <RingtonePlayer
          isPlaying={isRinging}
          ringtoneUrl="https://autocloudstorage.sgp1.digitaloceanspaces.com/Production/Ring_Tone/1749447419483-realme_ringtone.mp3"
        />
        {showOutgoing && (
          <OutgoingCallPopup
            flag={showOutgoing}
            attended={isAttended}
            setflag={setShowOutgoing}
            mobileNumber={mobileNumber}
            name={name}
            preferredExecutiveId={preferredExecutiveId}
            onTransferStarted={handleTransferStarted}
            transferStatusText={transferStatusText}
            transferPending={transferPending}
            onMergeTransfer={handleMergeTransfer}
            onReject={onRejectHangup}
          />
        )}
        {showInComming && (
          <CallPopup
            flag={showInComming}
            setflag={setShowInComming}
            attended={isAttended}
            mobileNumber={mobileNumber}
            name={name}
            handleCustomer={handleCustomerData}
            onReject={onRejectHangup}
            preferredExecutiveId={preferredExecutiveId}
            onTransferStarted={handleTransferStarted}
            transferStatusText={transferStatusText}
            transferPending={transferPending}
            onMergeTransfer={handleMergeTransfer}
          />
        )}
        <AppRouter />
      </callContext.Provider>
    </div>
  );
};

export default hot(withRouter(App));
